import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import GoogleSignInModal from '../components/homepage/GoogleSignInModal';
import './PortalStyles.css';

const PortalLogin = ({ standalone = true }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loginId, setLoginId] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showGoogleModal, setShowGoogleModal] = useState(false);

    const queryParams = new URLSearchParams(location.search);
    const redirectPath = queryParams.get('redirect') || '/patient-portal/dashboard';

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');

        try {
            const { api } = await import('../api/service');
            const response = await api.login(loginId, password);

            // Store token and user info
            localStorage.setItem('swastik_token', response.access_token);
            localStorage.setItem('portal_user', JSON.stringify(response.user));
            localStorage.setItem('swastik_role', 'patient');

            navigate(redirectPath);
        } catch (err) {
            setError(err.message || 'Invalid Patient ID or Password. Please try again or Register.');
        }
    };

    const loginForm = (
        <form className="portal-form-unified" onSubmit={handleLogin}>
            <div className="portal-form-group full-width">
                <label>Patient ID or Registered Email</label>
                <input
                    type="text"
                    required
                    placeholder="e.g. SWA-2026-XXXX or email@example.com"
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                />
            </div>

            <div className="portal-form-group full-width">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>Password</label>
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ background: 'none', border: 'none', color: '#0d9488', fontSize: '0.8rem', cursor: 'pointer', fontWeight: '600' }}
                    >
                        {showPassword ? 'Hide' : 'Show'}
                    </button>
                </div>
                <input
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                />
            </div>

            {error && (
                <div style={{
                    color: '#dc2626',
                    background: '#fef2f2',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    marginBottom: '1rem',
                    border: '1.5px solid #fee2e2'
                }}>
                    {error}
                </div>
            )}

            <button type="submit" className="portal-auth-btn">
                Secure Login &rarr;
            </button>

            <div style={{ display: 'flex', alignItems: 'center', margin: '1.5rem 0', gap: '1rem', color: '#94a3b8' }}>
                <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }}></div>
                <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>OR</span>
                <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }}></div>
            </div>

            <button
                type="button"
                className="google-login-btn"
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '0.85rem',
                    borderRadius: '12px',
                    border: '1.5px solid #e2e8f0',
                    background: '#fff',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: '#334155',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                }}
                onClick={() => setShowGoogleModal(true)}
            >
                <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.47 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335" />
                </svg>
                <span>Sign in with Google</span>
            </button>
        </form>
    );

    if (standalone) {
        return (
            <div className="portal-auth-layout" style={{ background: '#f8fafc', justifyContent: 'center', alignItems: 'center' }}>
                <div className="portal-auth-card" style={{ background: 'white', padding: '3rem', borderRadius: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.05)' }}>
                    <div className="portal-auth-header" style={{ textAlign: 'center' }}>
                        <h2>Portal Login</h2>
                        <p>Access your health records securely.</p>
                    </div>
                    {loginForm}
                </div>

                {showGoogleModal && (
                    <GoogleSignInModal
                        roleName="Patient"
                        roleValue="patient"
                        onClose={() => setShowGoogleModal(false)}
                        onFailure={(errMessage) => setError(errMessage)}
                    />
                )}
            </div>
        );
    }

    return (
        <>
            {loginForm}
            {showGoogleModal && (
                <GoogleSignInModal
                    roleName="Patient"
                    roleValue="patient"
                    onClose={() => setShowGoogleModal(false)}
                    onFailure={(errMessage) => setError(errMessage)}
                />
            )}
        </>
    );
};

export default PortalLogin;
